/**
 * BuildPathChooser — Build-tab front door on /fusion.
 * Demo training and live LE cases stay separate: demos use the sandbox + guides;
 * New case opens a real Case Canvas (CryptoFlow-style).
 */
import { FolderPlus, GraduationCap } from 'lucide-react';
import { isGuestDemo } from '../../../utils/guestDemo';

type Props = {
  onExploreDemos: () => void;
  onCreateCase: () => void;
};

export function BuildPathChooser({ onExploreDemos, onCreateCase }: Props) {
  const guest = isGuestDemo();

  return (
    <div
      className="h-full grid place-items-center p-6"
      data-testid="fusion-build-path-chooser"
      data-tour="fusion-build-path"
    >
      <div className="max-w-xl w-full">
        <h2 className="text-lg font-semibold text-center">How do you want to use Fusion?</h2>
        <p className="text-xs text-muted-foreground text-center mt-1.5 mb-6 leading-relaxed">
          Training demos and live investigations do not share a case. Pick one path —
          demo data never mixes into a real FIR case.
        </p>

        <div className="grid gap-3">
          <button
            type="button"
            onClick={onExploreDemos}
            data-testid="fusion-build-path-demos"
            data-tour="fusion-welcome-demos"
            className="flex items-start gap-3 text-left rounded-lg border border-border bg-card px-4 py-4 hover:border-primary hover:bg-secondary/40 transition-colors"
          >
            <GraduationCap className="w-5 h-5 text-primary mt-0.5 shrink-0" />
            <span>
              <span className="block text-sm font-semibold">Explore demos &amp; guides</span>
              <span className="block text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
                Synthetic training sandbox — Demo Guide walkthroughs, sample boards, and
                ingestion stages. Nothing here is a live LE case.
              </span>
            </span>
          </button>

          <button
            type="button"
            onClick={onCreateCase}
            data-testid="fusion-build-path-new-case"
            data-tour="fusion-welcome-new-case"
            className="flex items-start gap-3 text-left rounded-lg border border-primary/40 bg-primary/5 px-4 py-4 hover:border-primary hover:bg-primary/10 transition-colors"
          >
            <FolderPlus className="w-5 h-5 text-primary mt-0.5 shrink-0" />
            <span>
              <span className="block text-sm font-semibold">
                {guest ? 'Start blank board (sign in to save a case)' : 'Create a new case'}
              </span>
              <span className="block text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
                {guest
                  ? 'Author your own evidence without demo seed. Sign in to create a named LE case and persist it.'
                  : 'Name an LE case (title / FIR), then add phone, bank statements, subpoenas, notes, Instagram IDs on Build — your investigation, no demo seed.'}
              </span>
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
